// FLAG:useDocuAi — the invoice charges SEVERAL VAT rates but prints none per position.
//
// contractToAnalysis then has to put a rate into the position calculator anyway and uses the
// tenant's default. That number is our assumption, not the document's statement, and every
// figure derived from it inherits the assumption — including Σ positions net. The stated-totals
// block compares exactly that sum against the document's own net, so on such a document the
// comparison measures OUR guess and reports the gap as if the positions were wrong.
//
// The case that forced this (issue aaee49cf, EDEKA "Kassenbon 1.jpg"): eight gross lines adding
// up to the printed 14,84 € exactly, a VAT block stating 7 % and 19 %, no rate per item. All
// eight were grossed down at a flat 19 % → 12,47 €, and the review said that the positions totalled
// 12,47 € while the receipt stated 13,11 € net. Nothing was wrong with the positions; the rate split
// was unknown, and 0,64 € of input VAT would have been booked too high.
//
// So: while the rates are assumed, the positions-vs-net hint is REPLACED (not merely hidden) by
// one that names the real gap. The net-plus-VAT hint is untouched — it compares two figures the
// document states itself and stays meaningful.
//
// Two situations reach this, and they need different sentences:
//   * `bookedByTaxRate` — NOT ONE line stated a rate, so contractToAnalysis booked the document's
//     printed tax groups instead of the article lines (one position per rate). Nothing is assumed
//     any more; what the reviewer needs to know is why the Kassenbon's items are not the positions.
//   * otherwise — some lines state a rate and some do not; the rest were derived at the tenant
//     default, so the positions sum really is an assumption and the reviewer has to assign.
export interface AssumedLineTaxFact {
  statedRates?: number[] | null
  assumedRate?: number | null
  positionsWithoutRate?: number | null
  positionsTotal?: number | null
  bookedByTaxRate?: boolean | null
  taxRateGroups?: number | null
}

export const hasAssumedLineTax = (
  fact?: AssumedLineTaxFact | null,
): boolean => Array.isArray(fact?.statedRates)
  && fact!.statedRates!.length > 1
  && (fact!.positionsWithoutRate ?? 0) > 0

// Whether the positions on screen ARE the document's tax groups rather than its article lines.
export const isBookedByTaxRate = (
  fact?: AssumedLineTaxFact | null,
): boolean => hasAssumedLineTax(fact) && fact!.bookedByTaxRate === true

// "7 % und 19 %" — the rates as the document states them, for the hint text.
export const formatStatedRates = (
  fact?: AssumedLineTaxFact | null,
): string => {
  const rates = (fact?.statedRates || []).map(rate => `${rate} %`)
  if (rates.length < 2) return rates.join('')
  return `${rates.slice(0, -1).join(', ')} und ${rates[rates.length - 1]}`
}
